import {
  Box,
  Button,
  Card,
  CardMedia,
  FormHelperText,
  LinearProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import axios from "axios";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../core/store/store";

interface UploadImageTabProps {
  onSelectImage: (url: string, attribution?: string) => void;
}

export const UploadImageTab: React.FC<UploadImageTabProps> = ({ 
  onSelectImage 
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState("");
  const { apiUrl } = useSelector((state: RootState) => state.home);
  const theme = useTheme();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileError("");
    setUploadProgress(0);

    // Check file size (2MB limit)
    const maxSizeInBytes = 2 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      setFileError("Image must be less than 2MB.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await axios.post(`${apiUrl}/article/upload-image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setUploadProgress(percentCompleted);
        },
      });
      
      const result = response.data.result;
      setImageUrl(result.url);
      onSelectImage(result.url, "");
    } catch (error: unknown) {
      const errorMessage = error instanceof Error && 'response' in error 
        ? (error as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message 
        : undefined;
      setFileError(errorMessage || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        Upload an image directly from your computer (limit 2MB).
      </Typography>

      <Button
        variant="outlined"
        component="label"
        startIcon={<UploadIcon />}
        disabled={uploading}
        sx={{
          borderStyle: 'dashed',
          borderWidth: 2,
          py: 2,
          '&:hover': {
            borderStyle: 'dashed',
            borderColor: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
          }
        }}
      >
        Choose Image File
        <input
          type="file"
          hidden
          accept="image/*"
          onChange={handleFileChange}
          aria-label="Choose image file to upload"
        />
      </Button>

      {fileError && (
        <FormHelperText error>{fileError}</FormHelperText>
      )}

      {uploading && (
        <Box sx={{ width: '100%' }}>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}

      {imageUrl && (
        <Card sx={{ mt: 2 }}>
          <Box sx={{ p: 3 }}>
            <CardMedia
              component="img"
              image={imageUrl}
              alt="Uploaded Image"
              sx={{
                maxHeight: 300,
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          </Box>
          <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <Typography variant="body2" color="success.main">
              ✓ Image uploaded successfully!
            </Typography>
          </Box>
        </Card>
      )}
    </Stack>
  );
};